import { Component, OnInit, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  Logout,
  LogoutSuccess, LogoutToClientSelect,
  SupportLogout,
  SupportLogoutSuccess
} from '@app/core/authentication/authentication.actions';
import { LogModuleRoutes } from '@app/modules/log/log-routes';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { DrI18nService, DrLocale, DrLocales } from '@app/core/services/i18n/i18n.service';
import { CaseModuleRoutes } from '@app/modules/case/case-routes';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { SupportAccessDialogComponent } from '@app/shared/dialogs/support-access-dialog/support-access-dialog.component';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { SessionService } from '@app/core/services/session.service';
import { SessionExpirationEventData, SessionExpirationWebSocketService } from '@app/core/services/web-socket/session-expiration-web-socket.service';
import { User } from '@app/shared/models/user';
import { Subscription } from 'rxjs';
import { PersonalModuleRoutes } from '@app/modules/personal/personal-routes';
import { SettingsModuleRoutes } from '@app/modules/settings/settings-routes';
import { SystemIdDialogDialogComponent } from '@app/shared/dialogs/system-id-dialog/system-id-dialog.component';

export interface ToolBarMenuItem {
  id: string;
  icon?: string;
  text?: string;
  hide?: () => boolean;
  action(): any;
}

@Component({
  selector: 'app-tool-bar',
  templateUrl: './tool-bar.component.html',
  styleUrls: ['./tool-bar.component.scss']
})
export class ToolBarComponent extends BaseWithBundleComponent
                              implements OnInit {
  
  public menuButtons: ToolBarMenuItem[];
  public languageButtons: ToolBarMenuItem[];
  public activeLocale: DrLocale;
  public applicationName: string;
  protected userSessionExpirationSubscription: Subscription;

  constructor(
        protected componentServiceBundle: ComponentServiceBundle,
        public sessionService: SessionService,
        private store: Store<any>,
        private localeService: DrI18nService,
        private zone: NgZone,
        private sessionExpirationWebSocketService: SessionExpirationWebSocketService) {
    super(componentServiceBundle);
    this._idPrefix = 'toolbar.';
    this.applicationName = 'Regpro.';
  }

  public ngOnInit() {
    super.ngOnInit();

    this.activeLocale = DrI18nService.getActiveLocaleInformation();

    this.menuButtons = [{
        id: 'menu.btn.my_account',
        icon: 'account_circle',
        text:  marker('toolbar.menu.my_account'),
        hide: () => this.currentRouteIs(`/${PersonalModuleRoutes.MY_ACCOUNT}`),
        action: () => this.toMyAccount()
      }, {
        id: 'menu.btn.activity_log',
        icon: 'menu_book',
        text:  marker('toolbar.menu.client_activity_log'),
        hide: () => this.currentRouteIs(`/${LogModuleRoutes.CLIENT_ACTIVITY_LOG}`),
        action: () => this.toActivityLog()
      }, {
        id: 'menu.btn.billing_log',
        icon: 'receipt_long',
        text:  marker('toolbar.menu.billing_log'),
        // hide: () => !this.authenticationService.currentUserValue.hasPermissions(Permission.SMP_SETTINGS); // TODO: Consult BE for correct SMP permission (currently none provided)
        action: () => this.toBillingLog()
      }, {
        id: 'menu.btn.settings',
        icon: 'settings',
        text:  marker('toolbar.menu.settings'),
        hide: () => !this.authenticationService.currentUserValue
                                               .hasPermissions(this.Permission.SMP_SETTINGS),
        action: () => this.toSettings()
      }, {
        id: 'menu.btn.system_id',
        icon: 'memory',
        text:  marker('toolbar.menu.system_id'),
        action: () => this.openSystemIdDialog()
      }, {
        id: 'menu.btn.allow_support_access',
        icon: 'support_agent',
        text:  marker('toolbar.menu.allow_support_access'),
        action: () => this.openSupportAccessDialog()
      }, {
        id: 'menu.btn.client_select',
        icon: 'sync_alt',
        text: marker('toolbar.menu.client-select'),
        hide: () => !this.currentUser || !this.currentUser.isMultiClientUser,
        action: () => this.logOutToClientSelect()
      }, {
        id: 'menu.btn.log-out',
        icon: 'exit_to_app',
        text: marker('toolbar.menu.log-out'),
        action: () => this.logOut()
      }
    ];

    this.languageButtons = [
      {
        id: DrI18nService.DE.shortName,
        text: marker('toolbar.menu.language.' + DrI18nService.DE.shortName),
        hide: () => false,
        action: () => this.setActiveLocale(DrI18nService.DE)
      },
      {
        id: DrI18nService.EN.shortName,
        text: marker('toolbar.menu.language.' + DrI18nService.EN.shortName),
        hide: () => false,
        action: () => this.setActiveLocale(DrI18nService.EN)
      }
    ];

    this.handleSessionExpirationSubscription();
  }

  protected handleSessionExpirationSubscription(): void {

    const userSubscription = this.authenticationService.currentUser
                                                       .subscribe((user: User) => {
      if (user) {
        this.userSessionExpirationSubscription = this.sessionExpirationWebSocketService.socket
                                                                                       .subscribe((push: SessionExpirationEventData) => {
          if (this.authenticationService.userIsSupportUser) {
            this.store.dispatch(new SupportLogoutSuccess(push.data));
          } else {
            this.store.dispatch(new LogoutSuccess(push.data))
          }
        });

        this.subscriptions.push(this.userSessionExpirationSubscription);
      } else {
        if (this.userSessionExpirationSubscription) {
          this.subscriptions = this.subscriptions.filter(sub => sub !== this.userSessionExpirationSubscription);
          this.userSessionExpirationSubscription.unsubscribe();
          this.userSessionExpirationSubscription = null;
        }
      }
    });

    this.subscriptions.push(userSubscription);
  }

  protected currentRouteIs(route: string): boolean {
    const currentRoute = this.router.routerState.snapshot.url;
    return currentRoute === route;
  }

  protected getCaseId(): string {
      let currentRoute = this.router.routerState.snapshot.url;
      const id = currentRoute.substring(6, 42);
      currentRoute = currentRoute.replace(id, ':id');

      const mainCaseRoutes = [CaseModuleRoutes.QUICK_PROCESSING, CaseModuleRoutes.DETAILED_PROCESSING];

      const isCasePage = mainCaseRoutes.filter(route => {
        return currentRoute.includes(route);
      }).length > 0;

      return isCasePage ? id : '';
  }

  public toActivityLog(): void {
    // Activity log is opened in a new tab
    const caseId = this.getCaseId();
    const route = caseId ? LogModuleRoutes.CASE_ACTIVITY_LOG.replace(':id', caseId) : LogModuleRoutes.CLIENT_ACTIVITY_LOG;
    window.open(route);
  }

  public toBillingLog(): void {
    this.router.navigateByUrl(LogModuleRoutes.BILLING_LOG);
  }

  public toSettings(): void {
    this.router.navigateByUrl(SettingsModuleRoutes.SETTINGS);
  }

  private toMyAccount(): void {
    this.router.navigateByUrl(PersonalModuleRoutes.MY_ACCOUNT);
  }

  public openSystemIdDialog(): void {
    this.dialog.open(SystemIdDialogDialogComponent, {
      hasBackdrop: true,
      data: {}
    });
  }

  public openSupportAccessDialog(): void {
    const dialogRef = this.dialog.open(SupportAccessDialogComponent, {
      hasBackdrop: true,
    });

    dialogRef.afterClosed()
             .subscribe(dialogResult => {
        if (dialogResult) {
          this.showSimpleConfirmSnackbar(marker('toolbar.message.support_access_activated'));
        }
    });
  }

  public cancelSupportAccess(): void {
     this.sessionService.cancelSupportAccess()
                        .subscribe(() => {
        this.showSimpleConfirmSnackbar(marker('toolbar.message.support_access_cancelled'));
     });
  }

  public setActiveLocale(locale: DrLocale) {
    this.localeService.activeLocale = locale.fullName as DrLocales;
    this.zone.runOutsideAngular(() => {
      location.reload();
    });
  }

  public logOut() {
    this.authenticationService.userIsSupportUser ? this.store.dispatch(new SupportLogout()) :
                                                   this.store.dispatch(new Logout());
  }

  public logOutToClientSelect() {
    this.store.dispatch(new LogoutToClientSelect());
  }

}
